import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import config from '@/config';
import GalleryService from '@/services/gallery.service';
import ProfileAddButton from '@/shared-components/buttons/components/ProfileAddButton';
import ImageManager from '@/shared-components/forms/add-board-content/board-text-body/add-element/add-picture/ImageManager';
import PollDisplay from '@/shared-components/forms/add-board-content/board-text-body/add-element/add-polls/PollDisplay';
import PollManager from '@/shared-components/forms/add-board-content/board-text-body/add-element/add-polls/PollManager';
import QuizDisplay from '@/shared-components/forms/add-board-content/board-text-body/add-element/add-quiz/QuizDisplay';
import QuizManager from '@/shared-components/forms/add-board-content/board-text-body/add-element/add-quiz/QuizManager';
import TagPicker from '@/shared-components/forms/add-board-content/board-text-body/add-element/add-tags/TagPicker';
import { useTagging } from '@/shared-components/forms/add-board-content/board-text-body/add-element/add-tags/useTagging';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import Text from '@/shared-components/text/components/Text';

import { sanitizePostHtml } from '../saveFunctions';

import ArtworkDisplay from './add-element/add-board-artwork/ArtworkDisplay';
import ArtworkManager from './add-element/add-board-artwork/ArtworkManager';
import TextFormattingToolbar from './add-element/add-customization/TextFormattingToolbar';
import ImageDisplay from './add-element/add-picture/ImageDisplay';
import VideoDisplay from './add-element/add-video/VideoDisplay';
import VideoManager from './add-element/add-video/VideoManager';
import {
  ARTICLE_BODY_COMPOSITION,
  canAddInteractiveBlock,
  canAddMedia,
  usesLeadMedia,
} from './composition';
import {
  contentBlockKey,
  MEDIA_BLOCK_TYPES,
} from './contentDocument';
import { reduceDocument } from './documentReducer';
import TextBlock from './TextBlock';

import './BoardTextBody.css';
import './add-element/styles/EmbeddedContent.css';
import './styles/Mentions.css';

export default function BoardTextBody({
  formState,
  setFormState,
  editableRef,
  previewMode = false,
  composition = ARTICLE_BODY_COMPOSITION,
  hideAddButton = false,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const unsavedRef = useRef(false);
  const [selectedBlockKey, setSelectedBlockKey] = useState(null);
  const [isPollModalOpen, setIsPollModalOpen] = useState(false);
  const [isQuizModalOpen, setIsQuizModalOpen] = useState(false);
  const [isArtworkModalOpen, setIsArtworkModalOpen] = useState(false);
  const [triggerImageUpload, setTriggerImageUpload] = useState(null);
  const [triggerVideoUpload, setTriggerVideoUpload] = useState(null);
  const [showInsertOptions, setShowInsertOptions] = useState(false);
  const [pollToEdit, setPollToEdit] = useState(null);
  const [quizToEdit, setQuizToEdit] = useState(null);
  const {
    isOpen: isTagPickerOpen,
    close: closeTagPicker,
    replaceActiveTrigger,
  } = useTagging(editableRef);

  const {
    _id,
    post_structure: postStructure = [],
    post_texts: postTexts = [],
    post_polls: postPolls = [],
    post_quizzes: postQuizzes = [],
    post_pictures: postPictures = [],
    post_artworks: postArtworks = [],
    post_videos: postVideos = [],
  } = formState;
  const isLeadMediaLayout = usesLeadMedia(composition);
  const mediaGroupCount = postPictures.length + postArtworks.length + postVideos.length;
  const interactiveBlockCount = postPolls.length + postQuizzes.length;
  const canInsertMedia = canAddMedia(composition, mediaGroupCount);
  const canInsertInteractiveBlock = canAddInteractiveBlock(
    composition,
    interactiveBlockCount
  );

  const textsById = useMemo(
    () => Object.fromEntries(postTexts.map(text => [text.textId, text])),
    [postTexts]
  );
  const pollsById = useMemo(
    () => Object.fromEntries(postPolls.map(poll => [poll.pollId, poll])),
    [postPolls]
  );
  const quizzesById = useMemo(
    () => Object.fromEntries(postQuizzes.map(quiz => [quiz.quizId, quiz])),
    [postQuizzes]
  );
  const picturesById = useMemo(
    () => Object.fromEntries(postPictures.map(group => [group.groupId, group])),
    [postPictures]
  );
  const artworksById = useMemo(
    () => Object.fromEntries(postArtworks.map(group => [group.groupId, group])),
    [postArtworks]
  );
  const videosById = useMemo(
    () => Object.fromEntries(postVideos.map(group => [group.groupId, group])),
    [postVideos]
  );

  const mediaBlocks = isLeadMediaLayout
    ? postStructure.filter(block => MEDIA_BLOCK_TYPES.includes(block.type))
    : [];
  const bodyBlocks = isLeadMediaLayout
    ? postStructure.filter(block => !MEDIA_BLOCK_TYPES.includes(block.type))
    : postStructure;

  useEffect(() => {
    if (editableRef.current) {
      document.execCommand('defaultParagraphSeparator', false, 'div');
    }
  }, [editableRef]);

  useEffect(() => {
    const onBeforeUnload = event => {
      if (!previewMode && unsavedRef.current) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [previewMode]);

  const restoreInsertionPoint = () => {
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;

    const node = selection.getRangeAt(0).startContainer;
    const element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
    const block = element?.closest('[data-content-block]');
    setSelectedBlockKey(block?.dataset.contentBlock || null);
  };

  const dispatchDocument = action => {
    if (!previewMode) unsavedRef.current = true;
    setFormState(previous => reduceDocument(
      previous,
      { ...action, afterBlockKey: action.afterBlockKey ?? selectedBlockKey },
      composition
    ));
  };

  const handleImageRemove = (groupId, pictureId) => {
    dispatchDocument({ type: 'PICTURE_REMOVED', groupId, pictureId });
  };

  const handleArtworkRemove = (groupId, artworkId) => {
    dispatchDocument({ type: 'ARTWORK_REMOVED', groupId, artworkId });
  };

  const handleVideoRemove = groupId => {
    const video = videosById[groupId];
    if (video?.assetId) {
      GalleryService.deletePostVideoUpload(video.assetId).catch(() => {});
    }
    dispatchDocument({ type: 'VIDEO_REMOVED', groupId });
  };

  const handleCaptionChange = (groupId, pictureId, caption) => {
    dispatchDocument({
      type: 'PICTURE_CAPTION_UPDATED',
      groupId,
      pictureId,
      caption,
    });
  };

  const addTag = tag => {
    dispatchDocument({ type: 'TAG_ADDED', tag });
  };

  const removeTag = tagId => {
    dispatchDocument({ type: 'TAG_REMOVED', tagId });
  };

  const selectTag = tag => {
    const mention = replaceActiveTrigger(tag);
    if (!mention) return false;

    const textBlock = postTexts.find(text => (
      contentBlockKey('post_texts', text.textId) === mention.blockKey
    ));
    if (!textBlock) return false;

    commitTextBlock(textBlock.textId, mention.content);
    addTag(tag);
    return true;
  };

  const handlePollEdit = id => {
    const poll = pollsById[id];
    if (poll) {
      setPollToEdit(poll);
      setIsPollModalOpen(true);
    }
  };

  const handleQuizEdit = id => {
    const quiz = quizzesById[id];
    if (quiz) {
      setQuizToEdit(quiz);
      setIsQuizModalOpen(true);
    }
  };

  const castVote = async (pollId, optionIndex) => {
    const response = await fetch(
      `${config.apis.api.url}/post/${_id}/polls/${pollId}/vote`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ optionIndex }),
      }
    );
    const payload = await response.json();

    if (response.status === 403) {
      dispatchDocument({
        type: 'POLL_UPDATED',
        id: pollId,
        changes: { hasVoted: true },
      });
      return pollsById[pollId].options;
    }
    if (!response.ok) throw new Error(`Vote failed (${response.status})`);

    dispatchDocument({
      type: 'POLL_UPDATED',
      id: payload.poll.pollId,
      changes: payload.poll,
    });
    return payload.poll.options;
  };

  const castAnswer = async (quizId, answerIndex) => {
    const response = await fetch(
      `${config.apis.api.url}/post/${_id}/quizzes/${quizId}/answer`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answerIndex }),
      }
    );

    if (response.status === 403) return;
    if (!response.ok) throw new Error(`Answer failed (${response.status})`);

    dispatchDocument({
      type: 'QUIZ_UPDATED',
      id: quizId,
      changes: { hasAnswered: true, userAnswer: answerIndex },
    });
  };

  const commitTextBlock = (textId, html) => {
    if (previewMode) return;

    dispatchDocument({
      type: 'TEXT_COMMITTED',
      id: textId,
      content: sanitizePostHtml(html),
    });
  };

  const handleTextPaste = event => {
    if (previewMode || !event.clipboardData) return;
    const html = event.clipboardData.getData('text/html');
    const text = event.clipboardData.getData('text/plain');
    if (!html && !text) return;
    event.preventDefault();
    document.execCommand(
      'insertHTML',
      false,
      html ? sanitizePostHtml(html) : text.replace(/\n/g, '<br>')
    );
  };

  const renderBlock = block => {
    const key = contentBlockKey(block.type, block.id);

    if (block.type === 'post_texts') {
      const text = textsById[block.id];
      if (!text) return null;
      return (
        <TextBlock
          key={key}
          blockKey={key}
          content={text.content}
          editable={!previewMode}
          onChange={html => commitTextBlock(block.id, html)}
          onBlur={html => commitTextBlock(block.id, html)}
          onPaste={handleTextPaste}
        />
      );
    }

    if (block.type === 'post_polls') {
      const poll = pollsById[block.id];
      if (!poll) return null;
      return (
        <div key={key} data-content-block={key} data-atomic="true" contentEditable={false}>
          <PollDisplay
            pollId={poll.pollId}
            title={poll.question}
            options={poll.options}
            initialHasVoted={poll.hasVoted}
            initialUserAnswer={poll.userAnswer}
            previewMode={previewMode}
            onVote={poll.published ? castVote : undefined}
            onEditClick={() => handlePollEdit(poll.pollId)}
            hasAnybodyAnswered={poll.hasAnybodyAnswered}
          />
        </div>
      );
    }

    if (block.type === 'post_quizzes') {
      const quiz = quizzesById[block.id];
      if (!quiz) return null;
      return (
        <div key={key} data-content-block={key} data-atomic="true" contentEditable={false}>
          <QuizDisplay
            quizId={quiz.quizId}
            question={quiz.question}
            options={quiz.options}
            correctOption={quiz.correctOption}
            previewMode={previewMode}
            disableSelection
            initialHasAnswered={quiz.hasAnswered}
            userAnswer={quiz.userAnswer}
            onAnswer={quiz.published ? castAnswer : undefined}
            onEditClick={() => handleQuizEdit(quiz.quizId)}
            hasAnybodyAnswered={quiz.hasAnybodyAnswered}
          />
        </div>
      );
    }

    if (block.type === 'post_pictures') {
      const group = picturesById[block.id];
      if (!group) return null;
      return (
        <div key={key} data-content-block={key} data-atomic="true" contentEditable={false}>
          <ImageDisplay
            groupId={group.groupId}
            pictures={group.pictures}
            previewMode={previewMode}
            handleImageRemove={handleImageRemove}
            handleCaptionChange={handleCaptionChange}
          />
        </div>
      );
    }

    if (block.type === 'post_artworks') {
      const group = artworksById[block.id];
      if (!group) return null;
      return (
        <div key={key} data-content-block={key} data-atomic="true" contentEditable={false}>
          <ArtworkDisplay
            groupedArtworks={group}
            onArtworkRemove={handleArtworkRemove}
            previewMode={previewMode}
            navigate={navigate}
            location={location}
          />
        </div>
      );
    }

    if (block.type === 'post_videos') {
      const video = videosById[block.id];
      if (!video) return null;
      return (
        <div key={key} data-content-block={key} data-atomic="true" contentEditable={false}>
          <VideoDisplay
            groupId={video.groupId}
            postId={_id}
            video={video}
            previewMode={previewMode}
            onRemove={() => handleVideoRemove(video.groupId)}
            onStatusChange={data => dispatchDocument({
              type: 'VIDEO_UPDATED',
              groupId: video.groupId,
              changes: {
                status: data.status,
                isPlayable: data.isPlayable,
                isFullyProcessed: data.isFullyProcessed,
                processingPercent: data.processingPercent,
                playbackUrl: data.playbackUrl,
                thumbnailUrl: data.thumbnailUrl,
              },
            })}
          />
        </div>
      );
    }

    return null;
  };

  const openImage = () => {
    triggerImageUpload?.();
    setShowInsertOptions(false);
  };

  const openVideo = () => {
    triggerVideoUpload?.();
    setShowInsertOptions(false);
  };

  return (
    <div>
      {isLeadMediaLayout && mediaBlocks.length > 0 && (
        <div className="add-post__media-top-element">{mediaBlocks.map(renderBlock)}</div>
      )}

      <div
        className={`add-post__article-body${previewMode ? ' preview' : ''}`}
        ref={editableRef}
        onMouseDown={event => {
          if (previewMode || event.target !== event.currentTarget) return;
          event.preventDefault();
          const textBlocks = event.currentTarget.querySelectorAll(
            '.board-text-body__text-block'
          );
          textBlocks[textBlocks.length - 1]?.focus();
        }}
      >
        {bodyBlocks.map(renderBlock)}
      </div>

      {!previewMode && (
        <>
          <PollManager
            isPollModalOpen={isPollModalOpen}
            setIsPollModalOpen={setIsPollModalOpen}
            pollToEdit={pollToEdit}
            setPollToEdit={setPollToEdit}
            dispatch={dispatchDocument}
          />
          <QuizManager
            isQuizModalOpen={isQuizModalOpen}
            setIsQuizModalOpen={setIsQuizModalOpen}
            quizToEdit={quizToEdit}
            setQuizToEdit={setQuizToEdit}
            dispatch={dispatchDocument}
          />
          <ArtworkManager
            open={isArtworkModalOpen}
            close={() => setIsArtworkModalOpen(false)}
            dispatch={dispatchDocument}
            maxArtworks={composition.maxArtworksPerGroup}
          />
          <ImageManager
            setTriggerImageUpload={setTriggerImageUpload}
            dispatch={dispatchDocument}
          />
          <VideoManager
            setTriggerVideoUpload={setTriggerVideoUpload}
            dispatch={dispatchDocument}
            canUpload={() => canAddMedia(composition, mediaGroupCount)}
          />
        </>
      )}

      <TextFormattingToolbar
        editableRef={editableRef}
        previewMode={previewMode}
        enabled={composition.supportsTextFormatting}
      />

      <TagPicker
        open={isTagPickerOpen}
        close={closeTagPicker}
        tags={formState.post_tags || []}
        onSelect={selectTag}
        onRemove={removeTag}
      />

      {!hideAddButton && (
        <ProfileAddButton
          iconOnly
          alignRight
          ariaLabel="Add Content"
          onClick={() => {
            restoreInsertionPoint();
            setShowInsertOptions(true);
          }}
        />
      )}

      <GenericPopup
        open={showInsertOptions}
        darkerBlur
        className="remove-scrollbar board-text-body__insert-options-popup"
      >
        <div className="pop-up-artwork-create" onClick={() => setShowInsertOptions(false)}>
          <div className="pop-up-artwork-create-container" onClick={event => event.stopPropagation()}>
            {canInsertMedia && (
              <div className="pop-up-artwork-create-options" onClick={openImage}><Text>Image</Text></div>
            )}
            {canInsertMedia && (
              <div className="pop-up-artwork-create-options" onClick={openVideo}><Text>Video</Text></div>
            )}
            {canInsertMedia && (
              <div className="pop-up-artwork-create-options" onClick={() => {
                setIsArtworkModalOpen(true);
                setShowInsertOptions(false);
              }}><Text>Artwork</Text></div>
            )}
            {canInsertInteractiveBlock && (
              <div className="pop-up-artwork-create-options" onClick={() => {
                setIsPollModalOpen(true);
                setShowInsertOptions(false);
              }}><Text>Poll</Text></div>
            )}
            {canInsertInteractiveBlock && (
              <div className="pop-up-artwork-create-options" onClick={() => {
                setIsQuizModalOpen(true);
                setShowInsertOptions(false);
              }}><Text>Quiz</Text></div>
            )}
          </div>
        </div>
      </GenericPopup>
    </div>
  );
}
